import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UploadCloud, File, CheckCircle2, Loader2 } from 'lucide-react';
import { LoanDocument } from '../types';
interface UploadModalProps {
  isOpen: boolean;
  document: LoanDocument | null;
  onClose: () => void;
  onUpload: (documentId: string, file: File) => void;
}
export function UploadModal({
  isOpen,
  document,
  onClose,
  onUpload
}: UploadModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  if (!isOpen || !document) return null;
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };
  const handleUploadClick = () => {
    if (!selectedFile) return;
    setIsUploading(true);
    // Simulate network request
    setTimeout(() => {
      setIsUploading(false);
      onUpload(document.id, selectedFile);
      setSelectedFile(null);
      onClose();
    }, 1500);
  };
  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      onClose();
    }
  };
  return <AnimatePresence data-id="element-68">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-69">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={handleClose} data-id="element-70" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-71">
          <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-72">
            <div data-id="element-73">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-74">
                Upload Document
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-75">{document.name}</p>
            </div>
            <button onClick={handleClose} disabled={isUploading} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50" data-id="element-76">
              <X className="w-5 h-5" data-id="element-77" />
            </button>
          </div>

          <div className="p-6" data-id="element-78">
            {!selectedFile ? <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`
                  border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${isDragging ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                `} data-id="element-79">
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png" data-id="element-80" />
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-81">
                  <UploadCloud className="w-6 h-6" data-id="element-82" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1" data-id="element-83">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-slate-500" data-id="element-84">
                  PDF, JPG, or PNG (max. 10MB)
                </p>
              </div> : <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-85">
                <div className="flex items-center" data-id="element-86">
                  <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-87">
                    <File className="w-5 h-5 text-green-600" data-id="element-88" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-89">
                    <p className="text-sm font-medium text-slate-900 truncate" data-id="element-90">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-91">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button onClick={() => setSelectedFile(null)} disabled={isUploading} className="ml-4 p-2 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50" data-id="element-92">
                    <X className="w-4 h-4" data-id="element-93" />
                  </button>
                </div>
              </div>}
          </div>

          <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3" data-id="element-94">
            <button onClick={handleClose} disabled={isUploading} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 bg-slate-100 rounded-lg transition-colors disabled:opacity-50" data-id="element-95">
              Cancel
            </button>
            <button onClick={handleUploadClick} disabled={!selectedFile || isUploading} className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:bg-green-400 flex items-center" data-id="element-96">
              {isUploading ? <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-97" />
                  Uploading...
                </> : 'Upload Document'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}